import React from 'react';
import { useHistory } from 'react-router-dom';

import Menu from '~/components/core/Atomic/Menu';
import MenuItem from '~/components/core/Atomic/MenuItem';
import { useCms } from '~/components/hooks/useCms';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';

import { isLegacyOrgFeatureEnabled } from '../Utils';

import { useClaimCloseFlow } from './hooks/useClaimCloseFlow';
import { useClaim } from './ClaimContainer';
import WithConfirm from './ConfirmModal';
import { FsButton } from './core';
import ReopenClaimDialog from './ReopenClaimDialog';

import { useStyles } from '../assets/styles';

function CloseClaimContainer() {
  const { claim, onClaimUpdate } = useClaim();
  const { userOrganization } = useCms();
  const { classes } = useStyles();
  const history = useHistory();

  const [openDialog, setOpenDialog] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const {
    closeTypes,
    closeDisabledReasons,
    isConfigurableClosingReasonEnabled,
    isClosing,
    startClose,
    openConfigurableCloseDialog,
    closeDialogs,
  } = useClaimCloseFlow({
    claim,
    onClosed: async (closeNote) => {
      await onClaimUpdate();
      if (closeNote === 'Delete claim') {
        history.push('/home');
      }
    },
  });

  if (claim.is_closed) {
    return (
      <>
        <FsButton
          size="small"
          style={{ paddingTop: 3, paddingBottom: 3 }}
          variant="contained"
          color="primary"
          onClick={() => setOpenDialog(true)}
        >
          <span style={{ whiteSpace: 'nowrap' }}>Reopen Claim</span>
        </FsButton>
        <ReopenClaimDialog
          classes={classes}
          claim={claim}
          open={openDialog}
          onSubmit={() => onClaimUpdate().then(() => setOpenDialog(false))}
          onCancel={() => setOpenDialog(false)}
        />
      </>
    );
  }

  const disableClosingReasons = [...closeDisabledReasons];

  if (
    isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_WC_CLAIM_TYPE_REQUIRED_TO_CLOSE) &&
    claim.type === 'wc_claim' &&
    [null, '', 'pending'].includes(claim.incident.labeled_criteria)
  ) {
    disableClosingReasons.push('Claim type must be set');
  }

  const disableClosing = disableClosingReasons.length > 0;

  return (
    <>
      <FsButton
        size="small"
        style={{ paddingTop: 3, paddingBottom: 3 }}
        disabled={disableClosing || isClosing}
        variant="contained"
        color="primary"
        onClick={(e) => {
          if (isConfigurableClosingReasonEnabled) {
            openConfigurableCloseDialog();
          } else {
            setAnchorEl(e.currentTarget);
          }
        }}
        tooltipText={disableClosing ? disableClosingReasons.join(', ') : null}
      >
        <span style={{ whiteSpace: 'nowrap' }}>Close Claim</span>
      </FsButton>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setAnchorEl(null)}
      >
        {closeTypes.map((type, key) => {
          return (
            <WithConfirm
              key={key}
              title="Are you sure?"
              contentText={type.confirm_pop_up}
              primaryButtonName="Proceed"
              disableConfirm={!type.confirm_pop_up}
            >
              <MenuItem
                onClick={async () => {
                  await startClose(type);
                  setAnchorEl(null);
                }}
                disabled={isClosing}
              >
                <span dangerouslySetInnerHTML={{ __html: type.desc }} />
              </MenuItem>
            </WithConfirm>
          );
        })}
      </Menu>
      {closeDialogs}
    </>
  );
}

export default CloseClaimContainer;
